// SPDX-License-Identifier: AGPL-3.0-or-later
import KonvoConnections, {
	type KonvoCodexLogin,
	type KonvoCodexStatus,
} from '@app/features/konvo_connections/state/KonvoConnections';
import {useCallback, useEffect, useRef, useState} from 'react';
export function useKonvoCodexLink(onConnected: (status: KonvoCodexStatus) => void) {
	const [login, setLogin] = useState<KonvoCodexLogin | null>(null),
		[starting, setStarting] = useState(false),
		[error, setError] = useState<string | null>(null);
	const current = useRef<KonvoCodexLogin | null>(null),
		generation = useRef(0),
		checking = useRef(false),
		callback = useRef(onConnected);
	callback.current = onConnected;
	const cancel = useCallback(() => {
		generation.current++;
		if (current.current) KonvoConnections.cancelCodexLink(current.current.id);
		current.current = null;
		setLogin(null);
		setStarting(false);
	}, []);
	const start = useCallback(async () => {
		cancel();
		const attempt = generation.current;
		setStarting(true);
		setError(null);
		try {
			const next = await KonvoConnections.startCodexLink();
			if (generation.current !== attempt) {
				KonvoConnections.cancelCodexLink(next.id);
				return;
			}
			current.current = next;
			setLogin(next);
		} catch (error) {
			if (generation.current === attempt)
				setError(error instanceof Error ? error.message : 'Could not start Codex sign-in.');
		} finally {
			if (generation.current === attempt) setStarting(false);
		}
	}, [cancel]);
	const check = useCallback(async () => {
		const pending = current.current;
		if (!pending || checking.current) return;
		if (Date.parse(pending.expires_at) <= Date.now()) {
			cancel();
			setError('This sign-in code has expired. Get a new code to continue.');
			return;
		}
		checking.current = true;
		try {
			const result = await KonvoConnections.pollCodexLink(pending.id);
			if (current.current?.id !== pending.id) return;
			if (result.state === 'connected' && result.status?.connected) {
				current.current = null;
				setLogin(null);
				setError(null);
				callback.current(result.status);
			} else if (result.state === 'expired' || result.state === 'failed') {
				cancel();
				setError('Sign-in did not complete. Get a new code and try again.');
			} else setError(null);
		} catch {
			if (current.current?.id === pending.id)
				setError('Could not check sign-in. We’ll keep trying; you can also check again below.');
		} finally {
			checking.current = false;
		}
	}, [cancel]);
	useEffect(() => {
		void start();
		return () => {
			generation.current++;
			if (current.current) KonvoConnections.cancelCodexLink(current.current.id);
			current.current = null;
		};
	}, [start]);
	useEffect(() => {
		if (!login) return;
		const timer = window.setInterval(() => void check(), 2500);
		const focus = () => void check();
		window.addEventListener('focus', focus);
		return () => {
			window.clearInterval(timer);
			window.removeEventListener('focus', focus);
		};
	}, [login, check]);
	return {login, starting, error, start, check, cancel};
}
